import { useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

const INPUT = "bg-[#0B0C0E] border border-[#2D323E] rounded-sm px-3 py-2 text-sm text-slate-200 w-full font-mono";

export default function RequestAccess() {
  const [f, setF] = useState({ org_name: "", contact_name: "", contact_email: "", intended_use: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/enterprise/access-requests", f);
      setDone(data);
    } catch (err) { setError(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0B0C0E] flex items-center justify-center p-4" data-testid="request-access-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/assets/hussar-logo.png" alt="Hussar Treasury" className="w-64 mx-auto" />
          <div className="mt-3 text-[10px] font-mono tracking-widest text-slate-500 uppercase">Hussar Treasury Enterprise</div>
        </div>
        <div className="panel p-6">
          {done ? (
            <div className="text-center space-y-4" data-testid="ra-success">
              <ShieldCheck className="w-10 h-10 text-[#D4AF37] mx-auto" />
              <div className="text-sm text-slate-200 font-mono">REQUEST RECEIVED</div>
              <div className="text-xs text-slate-400 font-mono">Reference: <span className="text-[#D4AF37]">{done.request_id}</span></div>
              <p className="text-xs text-slate-500 leading-relaxed">
                Access is requested. Access is reviewed. Access is granted. It is never self-issued.
                If approved, a private one-time invitation is delivered to the approved contact email.
              </p>
              <Link to="/login" className="text-xs text-[#D4AF37] font-mono hover:underline" data-testid="ra-back-login">Back to sign in</Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3" data-testid="ra-form">
              <div className="text-sm font-mono text-slate-200 mb-1">Request Enterprise Access</div>
              <p className="text-[11px] text-slate-500 leading-relaxed">
                A request is not an account. Every request is reviewed internally; approval is never automatic.
              </p>
              <input data-testid="ra-org-input" required minLength={2} className={INPUT} placeholder="Organization legal name"
                     value={f.org_name} onChange={(e) => setF({ ...f, org_name: e.target.value })} />
              <input data-testid="ra-name-input" required className={INPUT} placeholder="Your full name"
                     value={f.contact_name} onChange={(e) => setF({ ...f, contact_name: e.target.value })} />
              <input data-testid="ra-email-input" required type="email" className={INPUT} placeholder="Work email (invitation binds to this address)"
                     value={f.contact_email} onChange={(e) => setF({ ...f, contact_email: e.target.value })} />
              <textarea data-testid="ra-use-input" rows={3} className={INPUT} placeholder="Intended use (optional)"
                        value={f.intended_use} onChange={(e) => setF({ ...f, intended_use: e.target.value })} />
              {error && <div className="text-xs text-red-400 font-mono" data-testid="ra-error">{error}</div>}
              <Button data-testid="ra-submit-btn" disabled={busy}
                      className="bg-[#D4AF37] text-black hover:bg-[#C5A059] font-mono text-xs w-full">
                {busy ? "SUBMITTING…" : "SUBMIT ACCESS REQUEST"}
              </Button>
              <div className="text-center">
                <Link to="/login" className="text-[11px] text-slate-500 font-mono hover:text-slate-300" data-testid="ra-login-link">Already approved? Sign in</Link>
              </div>
            </form>
          )}
        </div>
        <p className="mt-4 text-center text-[10px] font-mono text-slate-600 leading-relaxed">
          Hussar verifies ownership, provenance, controls, and settlement. Hussar does not take third-party custody.
        </p>
      </div>
    </div>
  );
}
